import { ConsoleLogger } from '@proposify/frontend-observability';
import { useCallback, useContext } from 'react';
import type { LiveblocksThread } from '../../../../../providers/CommentsContext';
import { useComments } from '../../../../../providers/CommentsContext';
import { useMultiEditor } from '../MultiEditorContext';
import { useActiveEditor } from '../ActiveEditorContext';
import type { ThreadOperationResult } from '../components/Comments/types';
import type { RoomType } from 'providers/RoomRegistrar';
import type { CommentBody } from '@liveblocks/client';
import { SelectionContext } from '../../SelectedBlockInfoProvider';
import { useQueryClient } from '@tanstack/react-query';
import { useViewerInfo } from '../../../../preview/identify-viewer/useViewerInfo';

import { resolveBlockId } from '../utils/editorBlockUtils';
import { createThreadInEditorPreview, deleteThreadInEditorPreview, updateThreadInEditorPreview } from '../utils/commentPreviewOperations';
import { ReactReduxContext } from 'react-redux';
import type { RootState } from '../../../grid/reduxStore/Store';
import { GUEST_EMAIL } from 'providers/LiveblocksProvider';

interface JSONMark {
  type: string;
  attrs?: Record<string, unknown>;
}

interface JSONContent {
  type?: string;
  marks?: JSONMark[];
  content?: JSONContent[];
  [key: string]: unknown;
}

export const useMultiEditorComments = (contentId: string) => {
  const {
    threads,
    isLoading,
    activeThreadId,
    setActiveThreadId,
    createThread,
    addComment,
    editComment,
    deleteComment,
    resolveThread,
    unResolveThread,
    deleteThread,
    focusThread,
    autoFocusThreadId,
    clearAutoFocusThreadId,
    userType,
    isPrivate,
  } = useComments();

  const { viewer, setCommentsStatus } = useViewerInfo();
  const queryClient = useQueryClient();

  const { executeOnAllEditors, getAllEditors, executeWhenEditorsReady, getEditor } = useMultiEditor();
  const { activeEditor } = useActiveEditor();
  const { selectedBlockIdByWrapper } = useContext(SelectionContext);

  const reduxContext = useContext(ReactReduxContext);
  const allSections = reduxContext?.store ? (reduxContext.store.getState() as RootState).gridBlockReducer.sections || [] : [];

  const isThreadInBlockContent = useCallback((threadId: string, blockContent: string): boolean => {
    try {
      const contentJson: JSONContent = JSON.parse(blockContent);
      const searchContent = (node: JSONContent): boolean => {
        if (!node) return false;

        if (node.marks) {
          const hasThread = node.marks.some((mark) => mark.type === 'commentThread' && mark.attrs?.threadId === threadId);
          if (hasThread) return true;
        }

        if (node.content) {
          return node.content.some((child) => searchContent(child));
        }

        return false;
      };

      return searchContent(contentJson);
    } catch (error) {
      ConsoleLogger.error('Failed to search thread in block content:', error, {
        threadId,
        blockContent: blockContent.substring(0, 100),
      });
      return false;
    }
  }, []);

  const findBlockIdForThread = useCallback(
    (threadId: string): string | null => {
      if (isPrivate) {
        for (const section of allSections) {
          for (const [blockId, block] of Object.entries(section.blocksContent)) {
            if (block.type === 'TEXT' && block.content && isThreadInBlockContent(threadId, block.content)) {
              return blockId;
            }
          }
        }
      } else {
        return (threads?.find((el) => el.id === threadId)?.metadata?.blockId as string) || null;
      }
      return null;
    },
    [allSections, isThreadInBlockContent, isPrivate, threads]
  );

  const isViewerUnidentified = !isPrivate && (!viewer?.id || !viewer?.email || viewer.email === GUEST_EMAIL);

  const handleCreateThread = useCallback(
    async (commentBody: CommentBody, roomType: RoomType): Promise<ThreadOperationResult> => {
      if (!activeEditor) {
        return { success: false, error: 'No active editor found' };
      }

      if (isViewerUnidentified) {
        setCommentsStatus('pending');
        return { success: false, error: 'identifyViewer' };
      }

      try {
        if (activeEditor.commands?.getThreadCountAtSelection) {
          activeEditor.commands?.getThreadCountAtSelection();
          const threadCount = activeEditor.storage.commentThread?.threadCountAtSelection;
          if (threadCount && threadCount >= 4) {
            return { success: false, error: 'maxThreadsReached' };
          }
        }

        const blockId = resolveBlockId(activeEditor, selectedBlockIdByWrapper);

        if (!blockId) {
          return { success: false, error: 'Block ID not found' };
        }

        const thread = await createThread(commentBody, roomType, blockId);

        if (!thread) {
          return { success: false, error: 'Failed to create thread in backend' };
        }

        let markApplied = false;

        if (!isPrivate) {
          const response = await createThreadInEditorPreview({
            activeEditor,
            blockId,
            threadId: thread.id,
            contentId,
            queryClient,
          });

          markApplied = !!response;
        } else {
          markApplied = activeEditor.commands?.addThread?.(thread as LiveblocksThread);
        }

        if (!markApplied) {
          return { success: false, error: 'Failed to apply thread mark to editor', threadId: thread.id };
        }

        return { success: true, threadId: thread.id };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error occurred',
        };
      }
    },
    [createThread, activeEditor, isViewerUnidentified]
  );

  const handleAddComment = useCallback(
    async (threadId: string, body: CommentBody, roomType: RoomType) => {
      try {
        if (isViewerUnidentified) {
          setCommentsStatus('pending');
          return;
        }
        const commentId = await addComment(threadId, body, roomType);
        return commentId;
      } catch (error) {
        ConsoleLogger.error('Failed to add comment:', error);
        return undefined;
      }
    },
    [addComment, isViewerUnidentified]
  );

  const handleEditComment = useCallback(
    async (threadId: string, commentId: string, body: CommentBody, roomType: RoomType) => {
      try {
        if (isViewerUnidentified) {
          setCommentsStatus('pending');
          return;
        }

        await editComment(threadId, commentId, body, roomType);
      } catch (error) {
        ConsoleLogger.error('Failed to edit comment:', error);
      }
    },
    [editComment, isViewerUnidentified]
  );

  const handleDeleteComment = useCallback(
    async (threadId: string, commentId: string, roomType: RoomType) => {
      try {
        if (isViewerUnidentified) {
          setCommentsStatus('pending');
          return;
        }

        await deleteComment(threadId, commentId, roomType);
      } catch (error) {
        ConsoleLogger.error('Failed to delete comment:', error);
      }
    },
    [deleteComment, isViewerUnidentified]
  );

  const handleResolveThread = useCallback(
    async (threadId: string, roomType: RoomType) => {
      try {
        if (isViewerUnidentified) {
          setCommentsStatus('pending');
          return;
        }

        const blockId = findBlockIdForThread(threadId);
        if (!blockId) {
          ConsoleLogger.warn('Thread not found in any block content:', threadId);
          return;
        }

        const editorId = `tiptap_editor_${blockId}`;
        const editorWithThread = getEditor(editorId);

        await resolveThread(threadId, roomType);

        if (editorWithThread) {
          if (!isPrivate) {
            await updateThreadInEditorPreview({
              activeEditor: editorWithThread,
              threadId,
              isResolved: true,
              contentId,
              queryClient,
            });
          } else {
            if (editorWithThread.commands.resolveThread) {
              editorWithThread.commands.resolveThread(threadId);
            }
          }
        } else {
          ConsoleLogger.warn('Editor not found or not ready for block:', blockId);
        }
      } catch (error) {
        ConsoleLogger.error('Failed to resolve thread:', error);
      }
    },
    [resolveThread, findBlockIdForThread, getEditor, isViewerUnidentified]
  );

  const handleMarkActiveThread = useCallback(
    async (threadId: string, roomType: RoomType) => {
      try {
        if (isViewerUnidentified) {
          setCommentsStatus('pending');
          return;
        }

        const blockId = findBlockIdForThread(threadId);
        if (!blockId) {
          ConsoleLogger.warn('Thread not found in any block content:', threadId);
          return;
        }

        const editorId = `tiptap_editor_${blockId}`;
        const editorWithThread = getEditor(editorId);

        await unResolveThread(threadId, roomType);

        if (editorWithThread) {
          if (!isPrivate) {
            await updateThreadInEditorPreview({
              activeEditor: editorWithThread,
              threadId,
              isResolved: false,
              contentId,
              queryClient,
            });
          } else {
            if (editorWithThread.commands.unresolveThread) {
              editorWithThread.commands.unresolveThread(threadId);
            }
          }
        } else {
          ConsoleLogger.warn('Editor not found or not ready for block:', blockId);
        }
      } catch (error) {
        ConsoleLogger.error('Failed to unresolve thread:', error);
      }
    },
    [unResolveThread, findBlockIdForThread, getEditor, isViewerUnidentified]
  );

  const handleDeleteThread = useCallback(
    async (threadId: string, roomType: RoomType) => {
      try {
        await deleteThread(threadId, roomType);

        const blockId = findBlockIdForThread(threadId);
        if (blockId) {
          const editorId = `tiptap_editor_${blockId}`;
          const editorWithThread = getEditor(editorId);

          if (editorWithThread) {
            if (!isPrivate) {
              await deleteThreadInEditorPreview({
                activeEditor: editorWithThread,
                threadId,
                contentId,
                queryClient,
              });
            } else {
              if (editorWithThread.commands.removeThread) {
                editorWithThread.commands.removeThread(threadId);
              }
            }
          }
        }
      } catch (error) {
        ConsoleLogger.error('Failed to delete thread:', error);
      }
    },
    [deleteThread, findBlockIdForThread, getEditor, isViewerUnidentified]
  );

  const handleFocusThread = useCallback(
    async (threadId: string) => {
      try {
        await focusThread(threadId);

        executeOnAllEditors((editor) => {
          if (editor.commands?.clearHighlight) {
            editor.commands.clearHighlight();
          }
        });

        const blockId = findBlockIdForThread(threadId);
        if (blockId) {
          const applyFocusToEditor = (editor: ReturnType<typeof getEditor> | undefined) => {
            if (!editor) {
              return false;
            }

            if (editor.commands?.highlightThread) {
              editor.commands.highlightThread(threadId);
            }

            const threadMarkElement = Array.from(editor.view?.dom?.querySelectorAll<HTMLElement>('[data-thread-id]') ?? []).find(
              (el) => el.dataset.threadId === threadId
            );
            if (threadMarkElement) {
              threadMarkElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest',
              });
              return true;
            }

            const blockElement = document.querySelector<HTMLElement>(`.block_${CSS.escape(blockId)}`);
            if (blockElement) {
              blockElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest',
              });
              return true;
            }

            return false;
          };

          const editorId = `tiptap_editor_${blockId}`;
          const editorWithThread = getEditor(editorId);
          if (applyFocusToEditor(editorWithThread)) {
            return;
          }

          executeWhenEditorsReady((editors) => {
            const editorContainingThread = editors.find((editor) => editor.commands?.hasThread?.(threadId));
            return applyFocusToEditor(editorContainingThread);
          });
        }
      } catch (error) {
        ConsoleLogger.error('Failed to focus thread:', error);
      }
    },
    [focusThread, executeOnAllEditors, findBlockIdForThread, getEditor, executeWhenEditorsReady]
  );

  const handleBlurThreads = useCallback(async () => {
    try {
      executeOnAllEditors((editor) => {
        if (editor.commands?.clearHighlight) {
          editor.commands.clearHighlight();
        }
      });
    } catch (error) {
      ConsoleLogger.error('Failed to blur threads:', error);
    }
  }, [executeOnAllEditors]);

  const setVisibleThreadsOnAllEditors = useCallback(
    (visibleThreads: LiveblocksThread[]) => {
      executeWhenEditorsReady((editors) => {
        const threadIds = visibleThreads.map((thread) => thread.id);

        editors.forEach((editor) => {
          if (editor.commands?.setVisibleThreads) {
            editor.commands.setVisibleThreads(threadIds);
          }
        });
      });
    },
    [executeWhenEditorsReady]
  );

  const setupThreadClickHandlerOnAllEditors = useCallback(
    (handler: (threadId: string) => void) => {
      executeWhenEditorsReady((editors) => {
        editors.forEach((editor) => {
          if (editor.storage.commentThread) {
            editor.storage.commentThread.onThreadClick = handler;
          }
        });
      });
    },
    [executeWhenEditorsReady]
  );

  const cleanupThreadClickHandlerOnAllEditors = useCallback(() => {
    executeWhenEditorsReady((editors) => {
      editors.forEach((editor) => {
        if (editor.storage.commentThread) {
          editor.storage.commentThread.onThreadClick = undefined;
        }
      });
    });
  }, [executeWhenEditorsReady]);

  const cleanupVisibleThreadsOnAllEditors = useCallback(() => {
    executeWhenEditorsReady((editors) => {
      editors.forEach((editor) => {
        if (editor.commands?.setVisibleThreads) {
          editor.commands.setVisibleThreads([]);
        }
      });
    });
  }, [executeWhenEditorsReady]);

  const getThreadsWithoutEditor = useCallback(() => {
    const threadsWithoutEditor: string[] = [];

    if (threads) {
      threads.forEach((thread) => {
        const blockIdFromContent = findBlockIdForThread(thread.id);

        if (!blockIdFromContent) {
          threadsWithoutEditor.push(thread.id);
        }
      });
    }

    return threadsWithoutEditor;
  }, [threads, findBlockIdForThread]);

  return {
    // Thread data
    threads,
    activeThreadId,
    autoFocusThreadId,
    isLoading,
    userType,
    isPrivate,

    // Actions
    createThread: handleCreateThread,
    addComment: handleAddComment,
    editComment: handleEditComment,
    deleteComment: handleDeleteComment,
    resolveThread: handleResolveThread,
    markActiveThread: handleMarkActiveThread,
    deleteThread: handleDeleteThread,
    focusThread: handleFocusThread,
    blurThreads: handleBlurThreads,
    clearAutoFocusThreadId,

    // UI actions
    setActiveThreadId,

    // Multi-editor specific actions
    setVisibleThreadsOnAllEditors,
    setupThreadClickHandlerOnAllEditors,
    cleanupThreadClickHandlerOnAllEditors,
    cleanupVisibleThreadsOnAllEditors,
    getAllEditors,
    getThreadsWithoutEditor,
  };
};
